import { useEffect, useMemo, useState } from "react"
import { router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { Button } from "@/components/ui/button"
import { HelpHint } from "@/components/ui/help-hint"
import { EndpointList } from "@/components/domain/endpoint-list"
import { EndpointForm } from "@/components/domain/endpoint-form"
import { EndpointTestPanel } from "@/components/domain/endpoint-test-panel"
import { EndpointErrorModal } from "@/components/domain/endpoint-error-modal"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import type { Endpoint, HttpMethod } from "@/types/endpoint"

type Props = {
  endpoints: Endpoint[]
  httpMethods: HttpMethod[]
  defaultSourceKey: string | null
  endpoints_stream: string
}

const DRAFT_ID = -1

// Reads ?endpoint=<key> from the current URL so a refresh / back-button
// snaps the operator back to the row they were editing. Falls back to
// the first endpoint when the param is missing or doesn't match. Keys
// (not ids) so the URL survives schema rebuilds and is shareable.
function initialSelectedId(endpoints: Endpoint[]): number | null {
  if (typeof window === "undefined") return endpoints[0]?.id ?? null
  const url = new URL(window.location.href)
  const wantedKey = url.searchParams.get("endpoint")
  if (wantedKey) {
    const found = endpoints.find((e) => e.key === wantedKey)
    if (found) return found.id
  }
  return endpoints[0]?.id ?? null
}

export default function EndpointsIndex({ endpoints, httpMethods, defaultSourceKey, endpoints_stream }: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(() => initialSelectedId(endpoints))
  const [isDraft, setIsDraft] = useState(false)
  const [errorModalEndpoint, setErrorModalEndpoint] = useState<Endpoint | null>(null)
  const [deleteCandidate, setDeleteCandidate] = useState<Endpoint | null>(null)
  const [deleting, setDeleting] = useState(false)

  // Live updates when other admins edit endpoints in this company.
  useInertiaCable(endpoints_stream, { only: ["endpoints"] })

  const selected = useMemo(() => {
    if (isDraft) return null
    return endpoints.find((e) => e.id === selectedId) ?? null
  }, [endpoints, selectedId, isDraft])

  // If the selected endpoint disappears (deleted by us or another admin),
  // fall back to the first one.
  useEffect(() => {
    if (!isDraft && selectedId && !endpoints.find((e) => e.id === selectedId)) {
      setSelectedId(endpoints[0]?.id ?? null)
    }
  }, [endpoints, selectedId, isDraft])

  // Reflect the selected endpoint in ?endpoint=<key> via replaceState so
  // a browser refresh re-mounts on the same row. replaceState (not push)
  // because we don't want every click to create a new history entry.
  useEffect(() => {
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (isDraft || selectedId == null) {
      if (!url.searchParams.has("endpoint")) return
      url.searchParams.delete("endpoint")
      window.history.replaceState({}, "", url.toString())
      return
    }
    const ep = endpoints.find((e) => e.id === selectedId)
    if (!ep) return
    if (url.searchParams.get("endpoint") === ep.key) return
    url.searchParams.set("endpoint", ep.key)
    window.history.replaceState({}, "", url.toString())
  }, [selectedId, isDraft, endpoints])

  const csrfToken = useMemo(() => {
    if (typeof document === "undefined") return ""
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content") ?? ""
  }, [])

  const startNew = () => {
    setIsDraft(true)
    setSelectedId(DRAFT_ID)
  }

  const handleSelect = (id: number) => {
    setIsDraft(false)
    setSelectedId(id)
  }

  const handleSaved = () => {
    // Inertia re-renders props with the new endpoints list. If we were
    // creating a draft, the new endpoint will appear; auto-select it next
    // tick by leaving selectedId as DRAFT_ID and letting the effect snap
    // back to the most-recent endpoint. Cleaner: just reset to first.
    setIsDraft(false)
    if (selectedId === DRAFT_ID) {
      setSelectedId(null)   // useEffect above will pick endpoints[0]
    }
  }

  const handleCancel = () => {
    setIsDraft(false)
    setSelectedId(endpoints[0]?.id ?? null)
  }

  const handleDelete = () => {
    if (!selected) return
    setDeleteCandidate(selected)
  }

  const confirmDelete = () => {
    if (!deleteCandidate) return
    setDeleting(true)
    router.delete(`/endpoints/${deleteCandidate.id}`, {
      preserveScroll: true,
      onSuccess: () => { setSelectedId(null) },
      onFinish: () => { setDeleting(false); setDeleteCandidate(null) },
    })
  }

  return (
    <AppShell>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <PageHeader
            eyebrow="Endpoints"
            title="Endpoints"
            subtitle="Manage and test your company's API endpoints."
            actions={<Button onClick={startNew} className="w-full sm:w-auto">+ New endpoint</Button>}
          />
        </div>
        <div className="hidden pt-9 md:block">
          <HelpHint
            heading="What are endpoints?"
            body={
              <>
                <p>
                  Endpoints are the partner APIs your skills can call via{" "}
                  <span className="font-mono text-app-text">call_company_endpoint</span> — coin balances, order
                  lookups, account state, anything the agent needs to read.
                </p>
                <p className="mt-2">
                  Each endpoint declares an HTTP method, a path template, request params, and a timeout.
                  The test panel runs them live so you can verify shape before wiring a skill.
                </p>
              </>
            }
          />
        </div>
      </div>

      {/* Mobile/tablet: stacked, natural height. Desktop: 2-col fixed-height
          dual-pane layout (the original look). lg:h-[…] only kicks in at the
          breakpoint where the 2-col grid is active so phone scroll feels
          natural. */}
      <section className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:h-[calc(100vh-220px)] lg:min-h-[640px] lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] lg:overflow-hidden">
        <EndpointList
          endpoints={endpoints}
          selectedId={selectedId}
          onSelect={handleSelect}
          onChartClick={setErrorModalEndpoint}
          onDelete={(e) => setDeleteCandidate(e)}
        />

        <div className="flex min-h-0 flex-col gap-4 lg:overflow-y-auto">
          {selectedId !== null ? (
            <EndpointForm
              endpoint={selected}
              httpMethods={httpMethods}
              isDraft={isDraft}
              onSaved={handleSaved}
              onCancel={handleCancel}
              onDelete={handleDelete}
            />
          ) : (
            <div className="flex items-center justify-center rounded-card border border-border bg-app-panel p-6 text-sm text-app-muted">
              Select an endpoint or click <span className="mx-1 font-medium text-app-text">+ New endpoint</span> to start.
            </div>
          )}

          <EndpointTestPanel
            endpoint={isDraft ? null : selected}
            defaultSourceKey={defaultSourceKey}
            csrfToken={csrfToken}
          />
        </div>
      </section>

      {errorModalEndpoint && (
        <EndpointErrorModal endpoint={errorModalEndpoint} onClose={() => setErrorModalEndpoint(null)} />
      )}

      <Dialog open={deleteCandidate !== null} onClose={() => !deleting && setDeleteCandidate(null)} size="sm">
        <DialogHeader title="Delete endpoint?" subtitle="This cannot be undone." />
        <DialogBody>
          {deleteCandidate?.referenceCount ? (
            <p className="text-sm text-status-error">
              <span className="font-medium">"{deleteCandidate.name}"</span> is referenced by{" "}
              {deleteCandidate.referenceCount} skill{deleteCandidate.referenceCount === 1 ? "" : "s"}.
              Detach the references first, then try again.
            </p>
          ) : (
            <p className="text-sm text-app-text">
              Permanently delete <span className="font-medium">"{deleteCandidate?.name}"</span>?
              The endpoint config and its 30-day error history will be removed.
            </p>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setDeleteCandidate(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirmDelete}
            disabled={deleting || !!deleteCandidate?.referenceCount}
            className="border border-status-error bg-status-error/90 hover:bg-status-error"
          >
            {deleting ? "Deleting…" : "Delete endpoint"}
          </Button>
        </DialogFooter>
      </Dialog>
    </AppShell>
  )
}
